import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { MAPS_API_KEY } from '@/shared/constants/advertiserCards';

interface GoogleMapsWrapperProps {
  children: React.ReactNode;
  mapContainerStyle?: React.CSSProperties;
  center?: { lat: number; lng: number };
  zoom?: number;
  onLoad?: (map: any) => void;
  loading?: boolean;
}

// Global flag to track if API is already loaded
let isApiLoaded = false;
let loadScriptPromise: Promise<void> | null = null;

const GoogleMapsWrapper: React.FC<GoogleMapsWrapperProps> = ({
  children,
  mapContainerStyle = { width: '100%', height: '100%' },
  center = { lat: 20.5937, lng: 78.9629 },
  zoom = 5,
  onLoad,
  loading = false,
}) => {
  const [isLoaded, setIsLoaded] = useState(isApiLoaded);
  const [loadError, setLoadError] = useState<string | null>(null);
  const mapRef = useRef<any>(null);

  // Load the Google Maps API only once
  useEffect(() => {
    if (isApiLoaded) {
      setIsLoaded(true);
      return;
    }

    if (loadScriptPromise) {
      loadScriptPromise
        .then(() => {
          setIsLoaded(true);
          isApiLoaded = true;
        })
        .catch(error => {
          setLoadError(error.message);
        });
      return;
    }

    // Create a promise to load the script
    loadScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        isApiLoaded = true;
        setIsLoaded(true);
        resolve();
      };

      script.onerror = () => {
        const error = new Error('Failed to load Google Maps API');
        setLoadError(error.message);
        reject(error);
      };

      document.head.appendChild(script);
    });

    loadScriptPromise
      .then(() => {
        setIsLoaded(true);
        isApiLoaded = true;
      })
      .catch(error => {
        setLoadError(error.message);
      });
  }, []);

  const [mapLoaded, setMapLoaded] = useState(false);

  const handleMapLoad = useCallback(
    (map: any) => {
      mapRef.current = map;

      // Add a small delay to ensure map is fully ready
      setTimeout(() => {
        setMapLoaded(true);
      }, 100);

      onLoad?.(map);
    },
    [onLoad]
  );

  if (loadError) {
    return (
      <div
        style={{
          ...mapContainerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          color: '#666',
        }}
      >
        Error loading Google Maps: {loadError}
      </div>
    );
  }

  if (loading || !isLoaded) {
    return (
      <div
        style={{
          ...mapContainerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        Loading map...
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={zoom}
      onLoad={handleMapLoad}
    >
      {(() => {
        console.log(
          'GoogleMapsWrapper: Rendering children, mapLoaded:',
          mapLoaded
        );
        return children;
      })()}
    </GoogleMap>
  );
};

export default memo(GoogleMapsWrapper);
